/*
 * JMGO responsive line-break lock
 *
 * Keeps manually specified PC line breaks in the same positions on SP.
 * Scope is limited to JMGO custom sections, so existing theme/product/blog
 * content outside those sections is not changed.
 */
(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';
  const mobileMedia = window.matchMedia(MOBILE_QUERY);

  // Current JMGO custom sections use jmgo-* or jr* classes on the section root.
  const ROOT_SELECTOR = [
    '#main section[class*="jmgo-"]',
    '#main section[class^="jr"]',
    '#main section[class*=" jr"]'
  ].join(',');

  // Text-like elements only. Container elements with block-level children are skipped.
  const TEXT_SELECTOR = 'h1,h2,h3,h4,h5,h6,p,li,dt,dd,figcaption,div,small';
  const BLOCK_CHILD_SELECTOR = 'div,p,h1,h2,h3,h4,h5,h6,ul,ol,li,dl,table,thead,tbody,tfoot,tr,td,th,section,article,aside,header,footer,nav,figure,blockquote,form';

  const originalInlineFont = new WeakMap();
  const managedElements = new Set();

  const hasMeaningfulRawNewline = (element) => {
    if (element.children.length > 0) return false;
    const text = (element.textContent || '').replace(/\r\n?/g, '\n').trim();
    return /\S[\t ]*\n[\t ]*\S/.test(text);
  };

  const preservesRawNewlines = (element) => {
    const whiteSpace = window.getComputedStyle(element).whiteSpace;
    return whiteSpace === 'pre' ||
      whiteSpace === 'pre-line' ||
      whiteSpace === 'pre-wrap' ||
      whiteSpace === 'break-spaces';
  };

  const convertRawNewlinesToBreaks = (element) => {
    if (!hasMeaningfulRawNewline(element) || !preservesRawNewlines(element)) {
      return false;
    }

    const raw = (element.textContent || '')
      .replace(/\r\n?/g, '\n')
      .trim();

    const lines = raw.split('\n');
    const fragment = document.createDocumentFragment();

    lines.forEach((line, index) => {
      if (index > 0) {
        fragment.appendChild(document.createElement('br'));
      }

      fragment.appendChild(
        document.createTextNode(line.trim())
      );
    });

    element.replaceChildren(fragment);

    return true;
  };

  const isTextLeaf = (element) => {
    if (
      element.matches(
        'h1,h2,h3,h4,h5,h6,p,li,dt,dd,figcaption,small'
      )
    ) {
      return true;
    }

    return !element.querySelector(
      `:scope > ${BLOCK_CHILD_SELECTOR
        .split(',')
        .join(', :scope > ')}`
    );
  };

  const rememberInlineFont = (element) => {
    if (originalInlineFont.has(element)) return;

    originalInlineFont.set(element, {
      value: element.style.getPropertyValue('font-size'),
      priority: element.style.getPropertyPriority('font-size')
    });
  };

  const restoreInlineFont = (element) => {
    const original = originalInlineFont.get(element);

    if (!original) return;

    if (original.value) {
      element.style.setProperty(
        'font-size',
        original.value,
        original.priority || ''
      );
    } else {
      element.style.removeProperty('font-size');
    }
  };

  const fitToSingleManualLineWidth = (element) => {
    rememberInlineFont(element);
    restoreInlineFont(element);

    if (
      !mobileMedia.matches ||
      element.classList.contains('jmgo-allow-mobile-wrap')
    ) {
      return;
    }

    const available = element.clientWidth;

    if (!available) return;

    // With nowrap + explicit <br>, scrollWidth is the width
    // of the longest manual line.
    const required = element.scrollWidth;

    if (required <= available + 0.5) return;

    const computedSize = Number.parseFloat(
      window.getComputedStyle(element).fontSize
    );

    if (
      !Number.isFinite(computedSize) ||
      computedSize <= 0
    ) {
      return;
    }

    // Keep the exact manual line breaks.
    // Only reduce size when the longest line would overflow.
    // No fixed px value is introduced, so the section's
    // Shopify font-size setting remains the base.
    let nextSize =
      computedSize * ((available - 1) / required);

    nextSize = Math.max(1, nextSize);

    element.style.setProperty(
      'font-size',
      `${nextSize.toFixed(3)}px`,
      'important'
    );

    // One correction pass handles rounding,
    // letter-spacing and font rendering differences.
    const correctedRequired = element.scrollWidth;

    if (correctedRequired > available + 0.5) {
      const correctedComputed = Number.parseFloat(
        window.getComputedStyle(element).fontSize
      );

      if (
        Number.isFinite(correctedComputed) &&
        correctedComputed > 0
      ) {
        nextSize =
          correctedComputed *
          ((available - 1) / correctedRequired);

        element.style.setProperty(
          'font-size',
          `${Math.max(1, nextSize).toFixed(3)}px`,
          'important'
        );
      }
    }
  };

  const prepareElement = (element) => {
    if (!(element instanceof HTMLElement)) return;
    if (!isTextLeaf(element)) return;

    if (
      element.closest(
        '[data-jmgo-mobile-wrap="allow"]'
      )
    ) {
      return;
    }

    // Existing <br> is the safest signal:
    // it is already a forced PC break.
    let hasExplicitBreak = Boolean(
      element.querySelector('br')
    );

    // Some custom sections render textarea newlines
    // via white-space: pre-line.
    // Convert only those already-visible newlines;
    // normal whitespace is left untouched.
    if (
      !hasExplicitBreak &&
      convertRawNewlinesToBreaks(element)
    ) {
      hasExplicitBreak = true;
    }

    if (!hasExplicitBreak) return;

    element.classList.add(
      'jmgo-mobile-pc-breaks'
    );

    managedElements.add(element);

    fitToSingleManualLineWidth(element);
  };

  const scan = (scope = document) => {
    const roots = [];

    if (
      scope instanceof Element &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    roots.push(
      ...scope.querySelectorAll(ROOT_SELECTOR)
    );

    roots.forEach((root) => {
      if (root.matches(TEXT_SELECTOR)) {
        prepareElement(root);
      }

      root
        .querySelectorAll(TEXT_SELECTOR)
        .forEach(prepareElement);
    });
  };

  const refitAll = () => {
    managedElements.forEach((element) => {
      if (
        !document.documentElement.contains(element)
      ) {
        managedElements.delete(element);
        return;
      }

      if (mobileMedia.matches) {
        fitToSingleManualLineWidth(element);
      } else {
        restoreInlineFont(element);
      }
    });
  };

  let resizeTimer = 0;

  const scheduleRefit = () => {
    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(
      refitAll,
      80
    );
  };

  const init = () => {
    scan(document);
    refitAll();

    window.addEventListener(
      'resize',
      scheduleRefit,
      { passive: true }
    );

    window.addEventListener(
      'orientationchange',
      scheduleRefit,
      { passive: true }
    );

    // Shopify theme editor re-renders sections
    // without a full page reload.
    document.addEventListener(
      'shopify:section:load',
      (event) => {
        scan(event.target || document);
        window.requestAnimationFrame(refitAll);
      }
    );

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        scan(event.target || document);
        window.requestAnimationFrame(refitAll);
      }
    );

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready
        .then(refitAll)
        .catch(() => {});
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();